import { useEffect, ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import HelpTooltip from '@/components/ui/help-tooltip';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Wand2, Trash2, Pencil, Copy } from 'lucide-react';
import { useForm, UseFormReturn } from 'react-hook-form';
import type { Eval } from '@/types/evals-types';
import {
  EvalPassFailConfig,
  EvalModel,
  EvalScoreConfig,
} from '@/types/evals-types';
import { deleteEvaluation } from '@/services/evaluation/delete-evaluation';
import { deleteEvalV2, isV2Eval } from '@/services/evaluation/eval-v2';
import { useNotification } from '@/components/notifications';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from '@/lib/zod-config';
import { MODELS_LABELS } from '@/constants/llm-models';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { PageTitle } from '@/components/page-title';
import type { EvalTypeMetadata } from '@/types/eval-v2-types';
import EvalDescriptionPanel from './eval-description-panel';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Update modelTypes to use EvalModel values and names
export const modelTypes = [
  { id: EvalModel.GPT_4O_MINI, name: MODELS_LABELS.GPT4Mini },
  { id: EvalModel.GPT_5_4, name: MODELS_LABELS.GPT54 },
  { id: EvalModel.GPT_5_MINI, name: MODELS_LABELS.GPT5mini },
  { id: EvalModel.CLAUDE_SONNET_40, name: MODELS_LABELS.Claude4 },
  { id: EvalModel.CLAUDE_SONNET_45, name: MODELS_LABELS.Claude45 },
  { id: EvalModel.CLAUDE_OPUS_46, name: MODELS_LABELS.ClaudeOpus46 },
  { id: EvalModel.CLAUDE_SONNET_46, name: MODELS_LABELS.ClaudeSonnet46 },
];

// Must match platform-api EVAL_NAME_MAX_LENGTH
export const EVAL_NAME_MAX_LENGTH = 100;

// Base schema for form validation
export const baseFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(
      EVAL_NAME_MAX_LENGTH,
      `Evaluation name must be ${EVAL_NAME_MAX_LENGTH} characters or less`,
    ),
  description: z.string().trim().min(1, 'Description is required'),
  model: z.nativeEnum(EvalModel),
});

// Union of all possible fields for both config types
export type EvalFormValues = z.infer<typeof baseFormSchema> & {
  eval_config: EvalScoreConfig | EvalPassFailConfig;
};

interface BaseEvalConfigProps {
  evalData: Eval;
  onSave: (data: Partial<Eval>) => void;
  onDuplicate: () => void;
  onDelete?: (evalId: string) => void;
  defaultValues: EvalFormValues;
  children: (
    form: UseFormReturn<EvalFormValues>,
    isEditing: boolean,
  ) => ReactNode;
  onGenerateFromDescription?: (form: UseFormReturn<EvalFormValues>) => void;
  title: string;
  /** Optional eval-type description shown directly under the title.
   *  Sourced from `useGetEvalTypes()` in the calling component. */
  description?: string;
  onSubmitTransform?: (data: EvalFormValues) => Partial<Eval>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema?: z.ZodType<EvalFormValues, any, any>;
  metadata?: EvalTypeMetadata;
}

export default function BaseEvalConfig({
  evalData,
  onSave,
  onDuplicate,
  onDelete,
  defaultValues,
  children,
  onGenerateFromDescription,
  title,
  description,
  onSubmitTransform,
  validationSchema,
  metadata,
}: BaseEvalConfigProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const form = useForm<EvalFormValues>({
    defaultValues,
    resolver: validationSchema ? zodResolver(validationSchema) : undefined,
  });
  const notification = useNotification();
  const { isEvalCriteriaAutogenEnabled } = useLaunchDarkly();
  const isExistingEval = !evalData.is_new;
  const [isEditing, setIsEditing] = useState(!isExistingEval);

  // Update form and edit state when selected eval changes
  useEffect(() => {
    if (evalData) {
      form.reset(defaultValues);
      setIsEditing(!!evalData.is_new);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evalData]);

  const canGenerateFromDescription =
    isEvalCriteriaAutogenEnabled && !!onGenerateFromDescription;

  const handleGenerate = () => {
    if (onGenerateFromDescription) {
      onGenerateFromDescription(form);
    }
  };

  const handleSubmit = async (data: EvalFormValues) => {
    if (onSubmitTransform) {
      onSave(onSubmitTransform(data));
    } else {
      onSave({ ...data, id: evalData.id });
    }
  };

  const handleDelete = async () => {
    if (!evalData.id) return;
    setIsDeleteLoading(true);

    try {
      if (isV2Eval(evalData)) {
        await deleteEvalV2(evalData.id);
      } else {
        await deleteEvaluation(evalData.id);
      }
      notification.success('Evaluation deleted successfully');
      if (onDelete) {
        onDelete(evalData.id);
      }
    } catch (error) {
      notification.error('Failed to delete evaluation');
      console.error('Error deleting evaluation:', error);
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <Form {...form}>
      <div
        className={`flex items-center justify-between ${description ? 'mb-1' : 'mb-6'}`}
      >
        <PageTitle title={title} />
        {isExistingEval && !isEditing && (
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={onDuplicate}
            >
              <Copy className='h-4 w-4' />
              Duplicate
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setIsEditing(true)}
            >
              <Pencil className='h-4 w-4' />
              Edit
            </Button>
          </div>
        )}
      </div>
      {description && (
        <p className='text-sm text-muted-foreground mb-6'>{description}</p>
      )}
      {metadata && <EvalDescriptionPanel metadata={metadata} />}
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-6'>
        <div className='space-y-4'>
          {/* Name field */}
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>
                    Evaluation Name
                  </FormLabel>
                  <HelpTooltip label='Evaluation name help'>
                    Give your evaluation a name for easy reference.
                  </HelpTooltip>
                </div>
                <FormControl>
                  <Input
                    {...field}
                    placeholder='e.g. creativity'
                    maxLength={EVAL_NAME_MAX_LENGTH}
                    disabled={!isEditing}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description field with generate button */}
          <div className='flex gap-2'>
            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <div className='flex items-center gap-2'>
                    <FormLabel className='label-required'>
                      Description
                    </FormLabel>
                    <HelpTooltip label='Description help'>
                      Provide details to give context for this evaluation.
                    </HelpTooltip>
                  </div>
                  <div className='flex gap-2'>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder='e.g. rates originality in approach, metaphor, or presentation'
                        disabled={!isEditing}
                      />
                    </FormControl>
                    {isEditing && canGenerateFromDescription && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type='button'
                              variant='outline'
                              size='icon'
                              onClick={handleGenerate}
                            >
                              <Wand2 className='h-4 w-4' />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Generate criteria from description</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Judge model selection */}
          <FormField
            control={form.control}
            name='model'
            render={({ field }) => (
              <FormItem>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>Judge Model</FormLabel>
                  <HelpTooltip label='Judge model help'>
                    Choose which model to use as the evaluator.
                  </HelpTooltip>
                </div>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={!isEditing}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder='Select a judge model' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {modelTypes.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Type-specific fields */}
          {children(form, isEditing)}
        </div>

        {/* Action buttons */}
        {isEditing && (
          <div className='flex justify-between items-center'>
            <div className='flex gap-2'>
              <Button type='submit'>
                {isExistingEval ? 'Save' : 'Create'}
              </Button>
              {isExistingEval && (
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    form.reset(defaultValues);
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
            {onDelete && isExistingEval && (
              <Button
                type='button'
                variant='ghost'
                className='text-muted-foreground hover:text-red-600'
                onClick={() => setIsDeleteModalOpen(true)}
              >
                <Trash2 className='h-4 w-4' />
                Delete
              </Button>
            )}
          </div>
        )}
      </form>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleteLoading}
        title='Delete Evaluation'
        description='Are you sure you want to delete this evaluation? All evaluation results and data will be permanently removed. This action cannot be undone.'
        buttonLabel='Delete Evaluation'
        handleDelete={handleDelete}
        handleCancel={() => setIsDeleteModalOpen(false)}
      />
    </Form>
  );
}
